import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  HOCs,
  Menu,
  MenuList,
  MenuListItem,
  MaterialIcon,
} from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, { ModalContent, ModalActions } from 'components/Modal';

import { PermissionTypes } from 'constants/enums';
import workspaceMessages from 'Workspace/messages';

import messages from './messages';
import { FormField, SelectLabel, Select, ReadOnlyInput } from './styles';

const { enhanceWithPopper } = HOCs;

const ROLE_OPTIONS = [
  { text: 'Owner', permission: PermissionTypes.OWNER },
  { text: 'Contributor', permission: PermissionTypes.CONTRIBUTOR },
  { text: 'Reviewer', permission: PermissionTypes.REVIEWER },
];

const RoleDropdownTrigger = ({ text, onClick, disabled }) => (
  <Button grey onClick={disabled ? undefined : onClick} disabled={disabled}>
    {text} <MaterialIcon id="arrow_drop_down" />
  </Button>
);

const RoleDropdownButton = enhanceWithPopper(RoleDropdownTrigger);

function getTypeOptions(workspacesList) {
  if (!workspacesList) return [];
  const list = typeof workspacesList.toJS === 'function' ? workspacesList.toJS() : workspacesList;
  if (!Array.isArray(list)) return [];
  return list.map((w) => ({ value: w.id, label: w.name }));
}

function ApproveRequestAccessModal({ visible, onDismiss, onFinalize, request, workspacesList }) {
  const typeOptions = getTypeOptions(workspacesList);
  const firstTypeValue = typeOptions.length ? typeOptions[0].value : '';
  const [type, setType] = useState(firstTypeValue);
  const [role, setRole] = useState(PermissionTypes.CONTRIBUTOR);

  useEffect(() => {
    if (!visible) {
      setType(firstTypeValue);
      setRole(PermissionTypes.CONTRIBUTOR);
    }
  }, [visible, firstTypeValue]);

  useEffect(() => {
    if (visible && typeOptions.length && !typeOptions.some((opt) => opt.value === type)) {
      setType(firstTypeValue);
    }
  }, [visible, typeOptions, type, firstTypeValue]);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      title={intl.formatMessage(messages.title)}
    >
      <ModalContent>
        <FormField>
          <SelectLabel>
            {intl.formatMessage(messages.requestedWorkspaceLabel)}
          </SelectLabel>
          <ReadOnlyInput
            id="approve-modal-requested-workspace"
            type="text"
            readOnly
            value={request?.workspace ?? ''}
          />
        </FormField>
        <FormField>
          <SelectLabel>
            {intl.formatMessage(messages.typeLabel)}
          </SelectLabel>
          <Select
            id="approve-modal-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label={intl.formatMessage(messages.typeLabel)}
          >
            {!typeOptions.length && (
              <option value="">{intl.formatMessage(messages.typePlaceholder)}</option>
            )}
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField>
          <SelectLabel id="approve-modal-role-label">
            {intl.formatMessage(messages.roleLabel)}
          </SelectLabel>
          <RoleDropdownButton
            placement="bottom-end"
            text={intl.formatMessage(workspaceMessages[`permission${ROLE_OPTIONS.find((r) => r.permission === role)?.text || 'Contributor'}`])}
            hidepopperonclick
            modifiers={{
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              flip: { enabled: false },
            }}
            aria-labelledby="approve-modal-role-label"
          >
            <Menu width={130}>
              <MenuList>
                {ROLE_OPTIONS.map((item) => (
                  <MenuListItem
                    key={item.permission}
                    onClick={() => setRole(item.permission)}
                    selected={item.permission === role}
                  >
                    {intl.formatMessage(workspaceMessages[`permission${item.text}`])}
                  </MenuListItem>
                ))}
              </MenuList>
            </Menu>
          </RoleDropdownButton>
        </FormField>
      </ModalContent>
      <ModalActions>
        <Button grey onClick={onDismiss}>
          {intl.formatMessage(messages.cancel)}
        </Button>
        <Button
          primary
          onClick={() => {
            onFinalize && onFinalize(request, type, role);
            onDismiss();
          }}
        >
          {intl.formatMessage(messages.finalize)}
        </Button>
      </ModalActions>
    </Modal>
  );
}

ApproveRequestAccessModal.propTypes = {
  visible: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  onFinalize: PropTypes.func,
  request: PropTypes.object,
  workspacesList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

ApproveRequestAccessModal.defaultProps = {
  visible: false,
  request: null,
  onFinalize: undefined,
  workspacesList: null,
};

export default ApproveRequestAccessModal;
